import { getBackendClient } from "@/lib/backend";
import { firstRecord, isRecord } from "@/lib/utils/query-helpers";

export interface CustomerDocumentView {
  customerName: string;
  date: string;
  documentKind: "estimate" | "invoice";
  lines: Array<{ lineTotal: string; name: string; quantity: string; unitPrice: string }>;
  total: string;
}

const VIEW_KEYS = ["customerName", "date", "documentKind", "lines", "total"] as const;
const LINE_KEYS = ["lineTotal", "name", "quantity", "unitPrice"] as const;

function hasOnlyKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  return Object.keys(value).every((key) => keys.includes(key)) && keys.every((key) => key in value);
}

function text(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isValidViewFields(record: Record<string, unknown>): boolean {
  return (
    hasOnlyKeys(record, VIEW_KEYS) &&
    ["estimate", "invoice"].includes(record.documentKind as string) &&
    text(record.customerName) &&
    text(record.date) &&
    text(record.total)
  );
}

function parseCustomerDocumentLine(line: unknown): CustomerDocumentView["lines"][number] | null {
  if (
    !isRecord(line) ||
    !hasOnlyKeys(line, LINE_KEYS) ||
    !LINE_KEYS.every((key) => text(line[key]))
  )
    return null;
  return line as CustomerDocumentView["lines"][number];
}

/** Rejects any payload that is not exactly the customer-safe backend DTO. */
export function parseCustomerDocumentView(value: unknown): CustomerDocumentView | null {
  const record = firstRecord(value);
  if (!record || !isValidViewFields(record)) return null;
  if (!Array.isArray(record.lines)) return null;
  const lines = record.lines.map(parseCustomerDocumentLine);
  return lines.some((line) => line === null)
    ? null
    : ({ ...record, lines: lines as CustomerDocumentView["lines"] } as CustomerDocumentView);
}

/** Produces an unsent customer-facing draft from the allowlisted DTO alone. */
export function renderCustomerMessage(view: CustomerDocumentView): string {
  const kind = view.documentKind === "estimate" ? "estimate" : "invoice";
  const lines = view.lines
    .map((line) => `• ${line.name}. ${line.quantity} × ${line.unitPrice} = ${line.lineTotal}`)
    .join("\n");
  return `Hi ${view.customerName},\n\nHere is your ${kind} dated ${view.date}:\n${lines}\n\nTotal: ${view.total}\n\nPlease let us know if you have any questions.`;
}

export async function fetchCustomerDocumentView(input: {
  proposalId: string;
  revision: number;
}): Promise<CustomerDocumentView> {
  const { data, error } = await getBackendClient().data.rpc("assistant_customer_document_view", {
    p_proposal_id: input.proposalId,
    p_revision: input.revision,
  });
  const response = firstRecord(data);
  const view = response?.ok === true ? parseCustomerDocumentView(response.view) : null;
  if (error || !view)
    throw new Error("Customer message is unavailable. Try again from this proposal.");
  return view;
}
